import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { DatePipe, JsonPipe } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';

import { FeedDiagnostics, GtfsDiagnosticsPayload } from '../../../shared/models/feed-diagnostics.model';
import { LabelBadge, LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { relativeTime } from '../../../shared/utils/relative-time';
import { isSnapshotStale } from '../util/feed-diagnostics.util';
import { asGtfsDiagnostics, gtfsRealtimeDot, gtfsStaticDot, gtfsStatusDot, snapshotAgeMinutes } from '../util/gtfs-diagnostics.util';

/**
 * Feed health card in the GTFS feed page's right column (sticky beside the
 * tabbed main card): the feed's latest GTFS diagnostics — overall badge
 * (Healthy / Degraded / Error, or Stale when the snapshot is old), GTFS's
 * message, a stale warning, Static schedule and Realtime sub-blocks, and a
 * footer with the snapshot age, monitored stops and a raw-payload toggle that
 * swaps the sub-blocks for the JSON. GTFS computes status/message itself —
 * nothing here re-derives health. The page owns the fetch (it also drives the
 * tab badges) and passes the snapshot in.
 */
@Component({
  selector: 'app-gtfs-feed-health',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, JsonPipe, LabelBadge],
  templateUrl: './gtfs-feed-health.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GtfsFeedHealthComponent {
  readonly snapshot = input<FeedDiagnostics | null>(null);
  readonly isLoading = input(false);
  readonly isCreate = input(false);

  readonly rawOpen = signal(false);

  readonly d = computed<GtfsDiagnosticsPayload | null>(() => asGtfsDiagnostics(this.snapshot()));
  readonly isStale = computed(() => isSnapshotStale(this.snapshot()));
  readonly staleMinutes = computed(() => {
    const s = this.snapshot();
    return s ? snapshotAgeMinutes(s) : 0;
  });
  /** Fetching but not succeeding: the last attempt is newer than the last success. */
  readonly realtimeLagging = computed(() => {
    const rt = this.d()?.realtime;
    return !!rt?.lastFetchAt && rt.lastFetchAt !== rt.lastSuccessAt;
  });

  /** Overall badge: Stale wins over the last reported status. */
  readonly overall = computed<{ key: string; dot: LabelBadgeDot | null }>(() => {
    if (!this.snapshot()) return { key: 'NONE', dot: null };
    if (this.isStale()) return { key: 'STALE', dot: 'red' };
    const d = this.d();
    return d ? { key: d.status, dot: gtfsStatusDot(d.status) } : { key: 'UNKNOWN', dot: 'yellow' };
  });

  readonly relativeTime = relativeTime;
  readonly staticDot = gtfsStaticDot;
  readonly realtimeDot = gtfsRealtimeDot;
}
